import React from 'react';
import {
    Box,
    Typography,
    Container,
    Paper,
    Divider,
} from '@mui/material';
import { useAppTheme } from '../App';

const PrivacyPolicy: React.FC = () => {
    const { getThemeStyles } = useAppTheme();
    const theme = getThemeStyles();

    return (
        <Box sx={{ 
            background: theme.background, 
            minHeight: '100vh',
            py: 4
        }}>
            <Container maxWidth="md">
                <Paper sx={{ 
                    background: theme.cardBackground,
                    border: `1px solid ${theme.borderColor}`,
                    borderRadius: 3,
                    p: { xs: 3, sm: 4 },
                    boxShadow: `0 4px 12px ${theme.shadowColor}`,
                }}>
                    <Typography 
                        variant="h3" 
                        sx={{ 
                            color: theme.textPrimary,
                            fontWeight: 700,
                            mb: 1,
                            textAlign: 'center'
                        }}
                    >
                        Privacy Policy
                    </Typography>
                    
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: theme.textSecondary,
                            textAlign: 'center',
                            mb: 4
                        }}
                    >
                        Last updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </Typography>

                    <Divider sx={{ borderColor: theme.borderColor, mb: 4 }} />

                    <Box sx={{ color: theme.textPrimary, '& h4': { color: theme.textPrimary, fontWeight: 600, mt: 3, mb: 2 } }}>
                        
                        <Typography variant="h4">1. Introduction</Typography>
                        <Typography paragraph>
                            Welcome to TheVisaAI (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;). We are committed to protecting your privacy and personal data. 
                            This Privacy Policy explains how we collect, use, and safeguard your information when you use our visa 
                            guidance and analysis services.
                        </Typography>

                        <Typography variant="h4">2. Data Controller Information</Typography>
                        <Typography paragraph>
                            <strong>Data Controller:</strong> TheVisaAI<br />
                            <strong>Location:</strong> United Kingdom<br />
                            <strong>Contact Email:</strong> thevisaai.team@gmail.com
                        </Typography>

                        <Typography variant="h4">3. Information We Collect</Typography>
                        <Typography paragraph>
                            We collect only the information necessary to provide our visa guidance services:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li><strong>Personal Information:</strong> Name and email address for account creation and service delivery</li>
                            <li><strong>Document Content:</strong> CV/resume, LinkedIn profiles, and supporting documents you upload for analysis</li>
                            <li><strong>Chat Data:</strong> Your conversations with our AI assistant for visa guidance</li>
                            <li><strong>Usage Data:</strong> Basic analytics on feature usage to improve our service</li>
                            <li><strong>Payment Information:</strong> Processed securely through RevenueCat and Paddle (we do not store payment details)</li>
                        </Box>

                        <Typography variant="h4">4. How We Use Your Information</Typography>
                        <Typography paragraph>
                            We use your information solely for providing our visa guidance services:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>Analyzing your profile for visa eligibility</li>
                            <li>Generating personalized visa application guidance</li>
                            <li>Creating custom personal statement templates</li>
                            <li>Providing AI-powered chat assistance</li>
                            <li>Managing your subscription and account</li>
                            <li>Improving our service quality</li>
                        </Box>
                        <Typography paragraph sx={{ fontStyle: 'italic', color: theme.textSecondary }}>
                            <strong>We do not use your data for our own purposes beyond service delivery.</strong>
                        </Typography>

                        <Typography variant="h4">5. Data Sharing and Disclosure</Typography>
                        <Typography paragraph>
                            We do not sell, trade, or otherwise transfer your personal information to third parties, except:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li><strong>Service Providers:</strong> Firebase (Google) for secure data storage, RevenueCat/Paddle for payment processing</li>
                            <li><strong>Legal Requirements:</strong> If required by law or to protect our rights</li>
                            <li><strong>AI Processing:</strong> OpenAI for document analysis and chat services (data is not retained by OpenAI)</li>
                        </Box>

                        <Typography variant="h4">6. Data Security</Typography>
                        <Typography paragraph>
                            We implement appropriate security measures to protect your personal information:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>Firebase Authentication and Firestore security rules</li>
                            <li>Encrypted data transmission (HTTPS/TLS)</li>
                            <li>Access controls limiting data access to authorized personnel only</li>
                            <li>Regular security assessments and updates</li>
                        </Box>

                        <Typography variant="h4">7. Your Rights (UK GDPR)</Typography>
                        <Typography paragraph>
                            As a UK-based service, we comply with UK GDPR. You have the right to:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li><strong>Access:</strong> Request a copy of your personal data</li>
                            <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                            <li><strong>Erasure:</strong> Request deletion of your personal data</li>
                            <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
                            <li><strong>Withdrawal:</strong> Withdraw consent for data processing at any time</li>
                        </Box>

                        <Typography variant="h4">8. Data Retention</Typography>
                        <Typography paragraph>
                            We retain your data only as long as necessary for service delivery:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li><strong>Account Data:</strong> Until you delete your account or request removal</li>
                            <li><strong>Document Data:</strong> Until you delete documents or close your account</li>
                            <li><strong>Chat History:</strong> Until you delete conversations or close your account</li>
                            <li><strong>Analytics Data:</strong> Anonymized data may be retained for service improvement</li>
                        </Box>

                        <Typography variant="h4">9. Cookies and Tracking</Typography>
                        <Typography paragraph>
                            We use essential cookies and analytics to:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>Maintain your login session</li>
                            <li>Remember your preferences (theme, settings)</li>
                            <li>Analyze service usage through Firebase Analytics</li>
                            <li>Process payments through RevenueCat</li>
                        </Box>

                        <Typography variant="h4">10. Third-Party Services</Typography>
                        <Typography paragraph>
                            Our service integrates with the following third-party providers:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li><strong>Firebase (Google):</strong> Authentication, database, and hosting</li>
                            <li><strong>OpenAI:</strong> AI-powered document analysis and chat services</li>
                            <li><strong>RevenueCat:</strong> Subscription management</li>
                            <li><strong>Paddle:</strong> Payment processing</li>
                        </Box>

                        <Typography variant="h4">11. International Transfers</Typography>
                        <Typography paragraph>
                            Your data may be transferred to and processed in countries outside the UK/EEA where our service 
                            providers operate. We ensure appropriate safeguards are in place for such transfers.
                        </Typography>

                        <Typography variant="h4">12. Changes to This Policy</Typography>
                        <Typography paragraph>
                            We may update this Privacy Policy from time to time. We will notify you of any material changes 
                            by posting the new Privacy Policy on this page and updating the &quot;Last updated&quot; date.
                        </Typography>

                        <Typography variant="h4">13. Contact Information</Typography>
                        <Typography paragraph>
                            If you have any questions about this Privacy Policy or our data practices, please contact us at:
                        </Typography>
                        <Typography paragraph sx={{ pl: 2 }}>
                            <strong>Email:</strong> thevisaai.team@gmail.com<br />
                            <strong>Subject Line:</strong> Privacy Policy Inquiry
                        </Typography>

                        <Divider sx={{ borderColor: theme.borderColor, my: 3 }} />
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default PrivacyPolicy;
